import React, { useEffect } from 'react';
import { Controller, UseFormMethods } from 'react-hook-form';
import { FormItem } from 'views/containers/form';
import { Checkbox } from 'semantic-ui-react';
import { Button } from 'lib/ui';
import { FooterFormWrapper } from '../style';
import { SIDEBAR_ACTIONS } from '../../AppointmentSidebar';
import { IOption } from 'model';
import {
  ADMIN_DEFAULT_CANCEL_REASON,
  APPOINTMENT_CATEGORY
} from 'utils/constants/lists';
import { clean } from 'utils/common';
import {
  getDateFromFormDate,
  prepareAdminForSubmissionV2,
  prepareEventForSubmissionV2
} from 'utils/mappers/request/events';
import { IEvent, IProvider } from 'model/v2';
import { getConflictsVariables, STEPS } from './utils';
import { DRIVE_TIME_APPT_IDS } from 'utils/constants/appointmentsTypes';
import { useLazyQuery } from 'react-apollo';
import { useApolloClient } from '@apollo/react-hooks';
import {
  VALIDATE_LOCATIONS,
  VALIDATE_MEAL_WAIVER
} from 'api/graphql/v2/queries/Location';
import { HAS_CONVERTED_EVENT_IN_SERIES } from 'api/graphql/v2/queries/Events';
import {
  CONVERTED_EVENT_MESSAGE,
  CONVERTED_SERIES_MESSAGE
} from 'utils/downstreams/DownStreamModalConfigurations';
interface Props {
  providersMap: Map<any, IProvider>;
  setMappedEvent: (val: any) => void;
  setFormData: (val: any) => void;
  doConflictsRequest: (val: any) => void;
  setStep: (val: number) => void;
  setConvertedMessage?: (val: string) => void;
  recurringOptions: IOption[];
  saving: boolean;
  action: string;
  methods: UseFormMethods;
  setSaving: (val: boolean) => void;
  isValidated?: boolean;
  isValidatedEvent?: boolean;
  abaObservers?: IEvent[];
  isSeriesAbaEvent?: boolean;
  seriesEventObservations?: IEvent[];
  timeValidate?: boolean;
  additionalProvidersList?: IProvider[];
  adminAddressisFetch?: boolean;
  // Blocks submit when a provider / appointment-type change is attempted on an
  // event that already has a started Bloom ABA note.
  bloomNoteBlocked?: boolean;
  onAlertsChange?: (alertData: {
    locationAlert: string | null;
    mealWaiverAlert: string | null;
    hasMealWaiver: boolean;
    setLocationAlert: (val: string | null) => void;
    setMealWaiverAlert: (val: string | null) => void;
  }) => void;
}

const ValidationFooter: React.FC<Props> = ({
  providersMap,
  setMappedEvent,
  setFormData,
  doConflictsRequest,
  setStep,
  setConvertedMessage,
  recurringOptions,
  saving,
  action,
  methods,
  setSaving,
  isValidated,
  isValidatedEvent,
  abaObservers,
  isSeriesAbaEvent,
  seriesEventObservations,
  additionalProvidersList,
  timeValidate,
  adminAddressisFetch,
  bloomNoteBlocked,
  onAlertsChange
}: Props) => {
  // const [providerId, setProviderId] = useState(0);
  //Sushma removed as getProviderAvailabilitywithEvents is not required August 22nd
  // const [
  //   getProviderAvailabilitywithEvents,
  //   { data: providerWithAvailabilityData }
  // ] = useLazyQuery(GET_PROVIDER_WITH_AVAILABILITY, {
  //   fetchPolicy: 'cache-and-network',
  //   onCompleted: () => {
  //     console.log('Inside the provider availability query');
  //     console.log(
  //       providerWithAvailabilityData,
  //       'Availability data from footer'
  //     );
  //   }
  // });

  const apolloClient = useApolloClient();
  const [alertContent, setAlertContent] = React.useState<string | null>(null);
  const [mealWaiverAlertContent, setMealWaiverAlertContent] = React.useState<
    string | null
  >(null);
  const [hasMealWaiver, setHasMealWaiver] = React.useState<boolean>(false);

  // Notify parent of alert state for rendering in scrollable area
  React.useEffect(() => {
    onAlertsChange?.({
      locationAlert: alertContent,
      mealWaiverAlert: mealWaiverAlertContent,
      hasMealWaiver,
      setLocationAlert: setAlertContent,
      setMealWaiverAlert: setMealWaiverAlertContent
    });
  }, [alertContent, mealWaiverAlertContent, hasMealWaiver, onAlertsChange]);

  const providerId = methods.watch('provider.id');
  const selectedClinicName = methods.watch('client.clinic.name');
  const selectedClinicId = methods.watch('clinic.id');
  const { startDate, endDate, locationType, selectedDay } = methods.watch([
    'startDate',
    'endDate',
    'locationType',
    'selectedDay'
  ]);

  // Use ref to track if component is mounted to prevent state updates after unmount
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // getLocations is called when these five of the watched fields are filled out by user
  const [
    validateAppointment,
    { loading: locationValidationInProgress }
  ] = useLazyQuery(VALIDATE_LOCATIONS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data: any) => {
      // If true, pop up alert to the scheduler. If false, do nothing.
      if (isMountedRef.current) {
        const results = data.checkLocationAvailability;
        setAlertContent(results.alertMessage);
      }
    },
    onError: (error: any) => {
      if (isMountedRef.current) {
        console.error('Query Error:', error.message);
      }
    }
  });

  // Meal waiver validation - checks for 5+ consecutive hours of non-admin appointments
  const [
    validateMealWaiver,
    { loading: mealWaiverValidationInProgress }
  ] = useLazyQuery(VALIDATE_MEAL_WAIVER, {
    fetchPolicy: 'no-cache',
    onCompleted: (data: any) => {
      if (isMountedRef.current) {
        const results = data.checkMealWaiverAlert;
        if (results.showAlert) {
          setMealWaiverAlertContent(results.alertMessage);
          setHasMealWaiver(results.hasMealWaiver);
        } else {
          setMealWaiverAlertContent(null);
        }
      }
    },
    onError: (error: any) => {
      console.error('Meal waiver query error:', error.message, error);
      if (isMountedRef.current) {
        console.error('Meal Waiver Query Error:', error.message);
      }
    }
  });

  useEffect(() => {
    // Clears alert when any of the watched fields are changed by user
    if (isMountedRef.current) {
      setAlertContent(null);
      setMealWaiverAlertContent(null);
    }

    // Location validation - requires clinic info (for client appointments)
    if (
      providerId !== undefined &&
      startDate !== undefined &&
      endDate !== undefined &&
      locationType !== undefined &&
      selectedClinicName !== undefined
    ) {
      validateAppointment({
        variables: {
          providerId: providerId,
          locationType: locationType,
          startTime: getDateFromFormDate(selectedDay, startDate),
          endTime: getDateFromFormDate(selectedDay, endDate),
          selectedClinicId: selectedClinicId
        }
      });
    }

    // Meal waiver validation - only needs provider and times (works for Admin too)
    // Now also works for EDIT actions by passing the eventId to exclude it from calculations
    if (
      providerId !== undefined &&
      startDate !== undefined &&
      endDate !== undefined
    ) {
      const eventId = methods.watch('id');
      const variables: any = {
        providerId: providerId,
        startTime: getDateFromFormDate(selectedDay, startDate),
        endTime: getDateFromFormDate(selectedDay, endDate)
      };

      // Include eventId when editing to exclude the current event from calculations
      if (
        action === SIDEBAR_ACTIONS.EDIT &&
        eventId !== undefined &&
        eventId !== null
      ) {
        variables.eventId = eventId;
      }

      validateMealWaiver({ variables });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    providerId,
    startDate,
    endDate,
    locationType,
    selectedClinicName,
    selectedDay,
    selectedClinicId,
    action
  ]);

  const submitForm = React.useCallback(
    async data => {
      setSaving(true);
      // Re-check conversion when saving an edit. The sidebar/context menu only
      // checks when first opened, so a note submitted while this form stays open
      // would otherwise let a converted appointment be rescheduled/edited. When
      // "Update the selected series" is chosen the affected set also includes
      // future events, so check the whole series, not just this instance.
      if (action === SIDEBAR_ACTIONS.EDIT && data?.id) {
        const isSeriesUpdate = data.recurrent === recurringOptions[1].value;
        try {
          const { data: seriesData } = await apolloClient.query({
            query: HAS_CONVERTED_EVENT_IN_SERIES,
            variables: { ids: [data.id], recurrent: isSeriesUpdate },
            fetchPolicy: 'network-only'
          });
          if (seriesData?.hasConvertedEventInSeries) {
            setConvertedMessage?.(
              isSeriesUpdate ? CONVERTED_SERIES_MESSAGE : CONVERTED_EVENT_MESSAGE
            );
            setStep(STEPS.CONVERTED_WARNING);
            setSaving(false);
            return;
          }
        } catch (error) {
          // If the check fails we fall through and let the edit attempt proceed,
          // surfacing any real error through the mutation's error handling.
        }
      }
      if (!data.cancelReason) {
        data.cancelReason = ADMIN_DEFAULT_CANCEL_REASON;
      }
      const isAdmin =
        data.apptCategory.split(': ')[1] === APPOINTMENT_CATEGORY[3].value;
      const isABA =
        data.apptCategory.split(': ')[1] === APPOINTMENT_CATEGORY[0].value;
      const isDriveTime = DRIVE_TIME_APPT_IDS[
        process.env.REACT_APP_STAGE!
      ].includes(data.appType);

      const mapped = clean(
        isAdmin
          ? prepareAdminForSubmissionV2(
              data,
              providersMap,
              additionalProvidersList,
              isDriveTime
            )
          : prepareEventForSubmissionV2(data)
      );
      setMappedEvent(mapped);

      setFormData(data);
      const isOverDriveTime = data?.driveTimeMileage > 50;
      //Sushma removed as getProviderAvailabilitywithEvents is not required August 22nd
      // getProviderAvailabilitywithEvents({
      //   variables: { id: Number(providerId) }
      // });
      const variables = isAdmin
        ? {}
        : getConflictsVariables(data, [mapped], recurringOptions)[0];
      if (isDriveTime && isOverDriveTime) {
        setStep(STEPS.MILEAGE_WARNING);
      } else if (
        isAdmin ||
        (action === SIDEBAR_ACTIONS.EDIT && !data?.dirtyEdit)
      ) {
        setStep(STEPS.SUBMIT);
      } else if (
        isABA &&
        action === SIDEBAR_ACTIONS.EDIT &&
        data?.dirtyEdit &&
        ((!isSeriesAbaEvent && abaObservers && abaObservers.length > 0) ||
          (isSeriesAbaEvent &&
            seriesEventObservations &&
            seriesEventObservations.length > 0))
      ) {
        setStep(STEPS.OBSERVER_WARNING);
      } else {
        doConflictsRequest({ variables });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setSaving,
      action,
      apolloClient,
      providersMap,
      recurringOptions,
      doConflictsRequest,
      setStep,
      setConvertedMessage,
      setMappedEvent,
      setFormData
    ]
  );

  const submitError = React.useCallback(() => {
    methods.trigger();
  }, [methods]);

  const {
    apptCategory,
    'drivetime-source-address-search-in-progress': awaitingRHFSetSourceAddress,
    'drivetime-destination-address-search-in-progress': awaitingRHFSetDestinationAddress,
    'mileage-request-in-progress': awaitingRHFMileage
  } = methods.watch([
    'apptCategory',
    'drivetime-source-address-search-in-progress',
    'drivetime-destination-address-search-in-progress',
    'mileage-request-in-progress'
  ]);

  return (
    <FooterFormWrapper>
      {action !== SIDEBAR_ACTIONS.EDIT &&
      apptCategory?.split(': ')[1] !== APPOINTMENT_CATEGORY[3].value ? (
        <FormItem
          optional={false}
          label="Save as Invisible"
          className="save-as-invisible-form-item"
        >
          <Controller
            name="isPendingConfirmation"
            control={methods.control}
            render={() => (
              <Checkbox
                style={{ marginLeft: 10 }}
                toggle
                onChange={(_, { checked }) => {
                  methods.setValue('isPendingConfirmation', checked);
                }}
              />
            )}
          />
        </FormItem>
      ) : (
        <FormItem
          optional={false}
          label=""
          className="save-as-invisible-form-item"
        >
          <></>
        </FormItem>
      )}
      <FormItem optional={false} label="">
        <Button
          className="large"
          onClick={methods.handleSubmit(submitForm, submitError)}
          type="submit"
          loading={saving}
          disabled={
            saving ||
            isValidated ||
            isValidatedEvent ||
            timeValidate ||
            bloomNoteBlocked ||
            locationValidationInProgress ||
            mealWaiverValidationInProgress ||
            adminAddressisFetch ||
            providersMap.size === 0 ||
            awaitingRHFSetSourceAddress ||
            awaitingRHFSetDestinationAddress ||
            awaitingRHFMileage
          }
        >
          Save
        </Button>
      </FormItem>
    </FooterFormWrapper>
  );
};
export default React.memo(ValidationFooter);
